import React, { useState, useEffect, useMemo } from 'react';
import dayjs from 'dayjs';
import { usePermissions } from '../../hooks/usePermissions';
import { useToast } from '../../hooks/useToast';
import Button from '../../components/shared/Button';
import StatusBadge from '../../components/shared/StatusBadge';
import Modal from '../../components/shared/Modal';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import ReservationModal from '../../components/business/ReservationModal';
import { getAllDocks } from '../../services/firestore/dockService';
import {
    getTransporterReservations,
    updateTransporterReservationStatus
} from '../../services/business/transporterPortalService';
import {
    DndContext,
    DragOverlay,
    useSensors,
    useSensor,
    PointerSensor,
    KeyboardSensor
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import TimelineBoard from '../../components/timeline/TimelineBoard';
import TimelineEvent from '../../components/timeline/TimelineEvent';
import { VEHICLE_STATUS, STATUS_COLORS, STATUS_LABELS } from '../../constants/statuses';
import { getDailyReservations, updateReservation, deleteReservation, addReservationNote } from '../../services/business/reservationService';
import { logAuditAction } from '../../services/logging/auditLogger';
import { getReservationsByDateRange } from '../../services/business/reservationService';
import { isTransporterMatch, buildUserIdentities, buildTransporterIdentities } from '../../services/business/transporterMatchingService';
import { getAllTransporters } from '../../services/firestore/transporterService';

const TransporterReservations = ({ userData }) => {
    const { user, isOwner } = usePermissions(userData);
    const { success, error: toastError } = useToast();

    const [loading, setLoading] = useState(true);
    const [reservations, setReservations] = useState([]);
    // Robust transporterId resolution
    const transporterId = user?.transporterId || user?.scope?.transporterId;

    // Filters & States
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'timeline'
    const [filterStatus, setFilterStatus] = useState('all');
    const [dateRange, setDateRange] = useState({
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().add(7, 'day').format('YYYY-MM-DD')
    });
    const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [viewStartHour, setViewStartHour] = useState(dayjs().hour());

    // UI States
    const [pxPerMin, setPxPerMin] = useState(2);
    const [dragActiveItem, setDragActiveItem] = useState(null);

    const [confirmConfig, setConfirmConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'warning',
        confirmText: 'Onayla',
        cancelText: 'Vazgeç',
        onConfirm: () => { }
    });

    const showConfirm = (config) => {
        setConfirmConfig({
            isOpen: true,
            title: config.title || 'Onay Gerekiyor',
            message: config.message || 'Bu işlemi yapmak istediğinize emin misiniz?',
            type: config.type || 'warning',
            confirmText: config.confirmText || 'Evet, Onayla',
            cancelText: config.cancelText || 'İptal',
            onConfirm: () => {
                config.onConfirm();
                setConfirmConfig(prev => ({ ...prev, isOpen: false }));
            }
        });
    };
    const timelineWrapperRef = React.useRef(null);

    // Confirm/Reject Modal
    const [selectedRes, setSelectedRes] = useState(null);
    const [actionType, setActionType] = useState(null); // 'approve' | 'reject'
    const [note, setNote] = useState('');
    const [isActionModalOpen, setIsActionModalOpen] = useState(false);

    // Edit Modal
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [docks, setDocks] = useState([]);

    const statusConfig = {};
    Object.keys(STATUS_COLORS).forEach(status => {
        statusConfig[status] = {
            color: STATUS_COLORS[status],
            textColor: '#fff',
            label: STATUS_LABELS[status] || status
        };
    });

    // Responsive Timeline Logic
    useEffect(() => {
        const updateWidth = () => {
            if (timelineWrapperRef.current) {
                const totalWidth = timelineWrapperRef.current.clientWidth;
                const contentWidth = totalWidth - 80;
                if (contentWidth > 50) {
                    const newPxPerMin = contentWidth / (8 * 60);
                    setPxPerMin(newPxPerMin);
                }
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        const observer = new ResizeObserver(updateWidth);
        if (timelineWrapperRef.current) observer.observe(timelineWrapperRef.current);
        return () => {
            window.removeEventListener('resize', updateWidth);
            observer.disconnect();
        };
    }, [viewMode]);

    useEffect(() => {
        if (user?.tenantId) {
            loadDocks();
        }
        if (transporterId) {
            loadData();
        } else if (user) {
            setLoading(false);
        }
    }, [user?.tenantId, transporterId, filterStatus, dateRange.start, dateRange.end, selectedDate, viewMode]);

    const loadDocks = async () => {
        try {
            const data = await getAllDocks(user.tenantId);
            setDocks(data);
        } catch (err) {
            console.error('Docks load error:', err);
        }
    };

    const loadData = async () => {
        const myIdentities = buildUserIdentities(userData);
        if (myIdentities.size === 0) {
            setReservations([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        try {
            // Identities already built above

            // Optionally enrich from master list if possible (helps if userData is incomplete)
            try {
                const tData = await getAllTransporters(user.tenantId);
                tData?.forEach(t => {
                    const tIdentities = buildTransporterIdentities(t);
                    if (Array.from(tIdentities).some(tid => myIdentities.has(tid))) {
                        tIdentities.forEach(id => myIdentities.add(id));
                    }
                });
            } catch (enrichErr) {
                console.warn('Enrichment failed, using profile only:', enrichErr);
            }

            let data;
            if (viewMode === 'timeline') {
                // Get ALL reservations for the day to show occupancy
                const dayData = await getDailyReservations(user.tenantId, selectedDate);
                // Mask non-owned reservations and filter out cancelled
                data = dayData
                    .filter(res => res.status !== VEHICLE_STATUS.CANCELLED && res.status !== 'cancelled' && res.status !== 'rejected')
                    .map(res => ({
                        ...res,
                        isMasked: !isTransporterMatch(res, myIdentities)
                    }));
            } else {
                // Fetch ALL for the range and filter robustly in-memory
                const allData = await getReservationsByDateRange(user.tenantId, dateRange.start, dateRange.end);

                data = allData.filter(r => {
                    // Match by identity
                    if (!isTransporterMatch(r, myIdentities)) return false;

                    // Filter by status if selected
                    if (filterStatus === 'pending') {
                        const pendingStatuses = ['pending', 'pending_approval', 'pending_transporter_confirmation'];
                        if (!pendingStatuses.includes(r.status)) return false;
                    } else if (filterStatus === 'confirmed') {
                        if (r.status !== VEHICLE_STATUS.APPROVED && r.status !== 'approved') return false;
                    } else if (filterStatus !== 'all') {
                        if (r.status !== filterStatus) return false;
                    }

                    return true;
                });

                // Manual Sort (Desc by time)
                data.sort((a, b) => (b.startTime || 0) - (a.startTime || 0));
            }
            setReservations(data);
        } catch (err) {
            console.error(err);
            toastError("Veriler yüklenirken hata oluştu.");
        } finally {
            setLoading(false);
        }
    };

    // DND Sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragStart = (event) => {
        setDragActiveItem(event.active.data.current?.reservation);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setDragActiveItem(null);

        if (!over || !over.id.startsWith('dock-')) return;

        const res = active.data.current?.reservation;
        if (!res || res.transporterId !== transporterId) return;

        const dockId = over.id.replace('dock-', '');
        const dock = docks.find(d => d.id === dockId);

        if (dock && res) {
            // Calculate dropped time
            const activeRect = active.rect.current.translated;
            const dropRect = over.rect;
            let relativeX = activeRect.left - dropRect.left;
            if (relativeX < 0) relativeX = 0;

            const visibleStartMs = dayjs(selectedDate).hour(viewStartHour).valueOf();
            const offsetMs = (relativeX / pxPerMin) * 60000;
            const snappedTime = Math.round((visibleStartMs + offsetMs) / (15 * 60000)) * (15 * 60000);

            const newStartTime = new Date(snappedTime);
            const durationMs = (new Date(res.endTime) - new Date(res.startTime));
            const newEndTime = new Date(snappedTime + durationMs);

            // Optimistic Update
            setReservations(prev => prev.map(r => r.id === res.id ? { ...r, dockId: dock.id, dockName: dock.name, startTime: newStartTime, endTime: newEndTime } : r));

            try {
                await updateReservation(res.id, {
                    dockId: dock.id,
                    dockName: dock.name,
                    startTime: newStartTime,
                    endTime: newEndTime
                }, user, user.tenantId);

                await logAuditAction(
                    user.tenantId,
                    user,
                    'RESERVATION_MOVE_TRANSPORTER',
                    `reservations/${res.id}`,
                    { dockName: dock.name, startTime: newStartTime }
                );

                success('Rezervasyon planı güncellendi.');
                loadData();
            } catch (err) {
                toastError("Taşıma hatası: " + err.message);
                loadData();
            }
        }
    };

    const handleTimeScroll = (direction) => {
        setViewStartHour(prev => {
            const next = prev + (direction * 4);
            return Math.max(0, Math.min(20, next));
        });
    };

    const handleActionClick = (res, type) => {
        setSelectedRes(res);
        setActionType(type);
        setNote('');
        setIsActionModalOpen(true);
    };

    const handleEditClick = (res) => {
        setSelectedRes(res);
        setIsEditModalOpen(true);
    };

    const handleConfirmAction = async (e) => {
        e.preventDefault();
        if (!selectedRes) return;

        const newStatus = actionType === 'approve' ? VEHICLE_STATUS.APPROVED : VEHICLE_STATUS.CANCELLED;
        try {
            await updateTransporterReservationStatus(selectedRes.id, newStatus, user.uid, note, user.tenantId);
            success(actionType === 'approve' ? 'Rezervasyon onaylandı.' : 'Rezervasyon iptal edildi.');
            setIsActionModalOpen(false);
            loadData();
        } catch (err) {
            toastError("İşlem başarısız: " + err.message);
        }
    };

    const handleEditSave = async (formData, isConfirm = false) => {
        try {
            // Reconstruct Dates using the selected date from modal
            const [h, m] = formData.startTime.split(':').map(Number);
            const startDate = dayjs(formData.bookingDate).hour(h).minute(m).second(0).toDate();
            const endDate = new Date(startDate.getTime() + formData.duration * 60000);

            const payload = {
                ...formData,
                startTime: startDate,
                endTime: endDate
            };

            // Remove form-only fields
            delete payload.duration;
            delete payload.bookingDate;
            const resNote = payload.note;
            delete payload.note;

            // Determine status
            let finalStatus = selectedRes.status;
            if (isConfirm) {
                finalStatus = VEHICLE_STATUS.APPROVED;
            } else {
                const initialDateStr = dayjs(selectedRes.startTime).format('YYYY-MM-DD');
                const hasFieldChanges =
                    payload.plateNumber !== selectedRes.plateNumber ||
                    payload.driverName !== selectedRes.driverName ||
                    formData.bookingDate !== initialDateStr ||
                    dayjs(payload.startTime).format('HH:mm') !== dayjs(selectedRes.startTime).format('HH:mm') ||
                    formData.duration !== dayjs(selectedRes.endTime).diff(selectedRes.startTime, 'minute');

                if (hasFieldChanges) {
                    finalStatus = VEHICLE_STATUS.TRANSPORTER_REVISED;
                }
            }

            if (resNote && resNote.trim()) {
                await addReservationNote(selectedRes.id, resNote, userData, user.tenantId);
            }

            // Sync with service call
            await updateReservation(selectedRes.id, {
                ...payload,
                status: finalStatus,
                lastActionBy: user.uid,
                lastActionRole: userData.role
            }, user, user.tenantId);

            success(isConfirm ? 'Rezervasyon onaylandı.' : 'Rezervasyon güncellendi.');
            setIsEditModalOpen(false);
            loadData();
        } catch (err) {
            toastError("Güncelleme hatası: " + err.message);
        }
    };

    const handleEditDelete = async (id) => {
        showConfirm({
            title: 'Rezervasyonu Sil',
            message: 'Bu rezervasyonu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.',
            type: 'error',
            confirmText: 'Evet, Sil',
            onConfirm: async () => {
                try {
                    await deleteReservation(id, user.tenantId);

                    await logAuditAction(
                        id,
                        'RESERVATION',
                        'RESERVATION_DELETE',
                        { plateNumber: selectedRes?.plateNumber || '-' },
                        user,
                        'TransporterPortal',
                        user.tenantId
                    );

                    success('Rezervasyon silindi.');
                    setIsEditModalOpen(false);
                    loadData();
                } catch (err) {
                    toastError("Silme hatası: " + err.message);
                }
            }
        });
    };

    if (!transporterId && !isOwner) {
        return <div className="p-xl text-center" style={{ color: 'var(--text-primary)' }}>Yetki yok. Bu sayfa sadece Nakliyeci yetkisine sahip kullanıcılar içindir.</div>;
    }

    if (!transporterId && isOwner) {
        return (
            <div className="container p-xl text-center" style={{ color: 'var(--text-primary)' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏢</div>
                <h2>Transporter Bağlantısı Yok</h2>
                <p style={{ color: 'var(--text-secondary)', maxWidth: '600px', margin: '0 auto' }}>
                    Sistem yöneticisi (Owner) olarak bu sayfaya erişim yetkiniz var ancak mevcut hesabınız herhangi bir
                    Nakliyeci firması ile ilişkilendirilmemiş. Rezervasyonları görmek için bir Nakliyeci hesabıyla
                    giriş yapmalı veya kendi hesabınızı bir nakliyeci firmasına atamalısınız.
                </p>
            </div>
        );
    }

    return (
        <div className="container pt-lg">
            <div className="flex justify-between items-center mb-lg">
                <div>
                    <h1 style={{ color: 'var(--text-primary)' }}>Rezervasyonlar</h1>
                    <p style={{ color: 'var(--text-secondary)' }}>Yeni ve geçmiş taşıma randevularını yönetin.</p>
                </div>
                <div className="flex gap-sm items-center">
                    {/* View Mode Toggle */}
                    <div className="flex bg-slate-800/50 p-1 rounded-xl mr-md">
                        <Button
                            variant={viewMode === 'list' ? 'primary' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('list')}
                            style={{ borderRadius: '8px' }}
                        >Liste</Button>
                        <Button
                            variant={viewMode === 'timeline' ? 'primary' : 'ghost'}
                            size="sm"
                            onClick={() => setViewMode('timeline')}
                            style={{ borderRadius: '8px' }}
                        >Zaman Çizelgesi</Button>
                    </div>

                    {viewMode === 'list' ? (
                        <>
                            <div className="flex gap-xs items-center mr-md">
                                <Button
                                    variant={filterStatus === 'all' ? 'primary' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilterStatus('all')}
                                >Tümü</Button>
                                <Button
                                    variant={filterStatus === 'pending' ? 'primary' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilterStatus('pending')}
                                >Onay Bekleyen</Button>
                                <Button
                                    variant={filterStatus === 'confirmed' ? 'primary' : 'outline'}
                                    size="sm"
                                    onClick={() => setFilterStatus('confirmed')}
                                >Onaylananlar</Button>
                            </div>

                            <div className="flex items-center gap-xs ml-md" style={{ borderLeft: '1px solid var(--border-color)', paddingLeft: '1.5rem' }}>
                                <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-secondary)', textTransform: 'uppercase', marginRight: '5px' }}>Tarih Aralığı:</span>
                                <input
                                    type="date"
                                    className="glass-input"
                                    style={{ padding: '6px 12px', fontSize: '13px', height: '36px', borderRadius: '10px' }}
                                    value={dateRange.start}
                                    onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                                />
                                <span style={{ color: 'var(--text-secondary)' }}>-</span>
                                <input
                                    type="date"
                                    className="glass-input"
                                    style={{ padding: '6px 12px', fontSize: '13px', height: '36px', borderRadius: '10px' }}
                                    value={dateRange.end}
                                    onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="flex items-center gap-sm">
                            <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--text-secondary)', textTransform: 'uppercase' }}>Seçili Tarih:</span>
                            <input
                                type="date"
                                className="glass-input"
                                style={{ padding: '6px 12px', fontSize: '13px', height: '36px', borderRadius: '10px' }}
                                value={selectedDate}
                                onChange={e => setSelectedDate(e.target.value)}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="card-premium glow-theme" style={{ padding: 0, overflow: 'hidden' }}>
                {loading ? (
                    <div className="p-xl text-center" style={{ color: 'var(--text-primary)' }}>Yükleniyor...</div>
                ) : viewMode === 'list' ? (
                    reservations.length === 0 ? (
                        <div className="p-xl text-center" style={{ color: 'var(--text-secondary)', opacity: 0.6 }}>
                            Kayıt bulunamadı.
                        </div>
                    ) : (
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Tarih/Saat</th>
                                    <th>Rezervasyon No</th>
                                    <th>Plaka</th>
                                    <th>Sürücü</th>
                                    <th>Depo/Rampa</th>
                                    <th>Durum</th>
                                    <th>İşlemler</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reservations.map(res => (
                                    <tr key={res.id}>
                                        <td>
                                            <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                                                {res.startTime ? new Date(res.startTime).toLocaleDateString('tr-TR') : '-'}
                                            </div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.7 }}>
                                                {res.startTime ? new Date(res.startTime).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                            </div>
                                        </td>
                                        <td style={{ fontFamily: 'monospace', color: 'var(--primary-color)', fontWeight: 'bold' }}>
                                            {res.reservationNumber || 'REZ-NEW'}
                                        </td>
                                        <td style={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                                            {res.plateNumber || '-'}
                                        </td>
                                        <td style={{ color: 'var(--text-primary)' }}>
                                            {res.driverName || '-'}
                                        </td>
                                        <td>
                                            <div style={{ color: 'var(--text-primary)' }}>{res.factoryName || 'Fabrika'}</div>
                                            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', opacity: 0.7 }}>{res.dockName}</div>
                                        </td>
                                        <td><StatusBadge status={res.status} /></td>
                                        <td>
                                            <div className="flex gap-xs">
                                                {(res.status === 'pending' || res.status === 'pending_transporter_confirmation' || res.status === 'pending_approval') && (
                                                    <Button size="xs" variant="success" onClick={(e) => { e.stopPropagation(); handleActionClick(res, 'approve'); }}>Onayla</Button>
                                                )}
                                                <Button size="xs" variant="secondary" onClick={(e) => { e.stopPropagation(); handleEditClick(res); }}>Düzenle</Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                ) : (
                    <div ref={timelineWrapperRef} style={{ height: 'calc(100vh - 250px)', display: 'flex' }}>
                        <DndContext
                            sensors={sensors}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                        >
                            <TimelineBoard
                                docks={docks}
                                reservations={reservations}
                                visibleStart={dayjs(selectedDate).hour(viewStartHour).toDate()}
                                visibleDurationMins={8 * 60}
                                pxPerMin={pxPerMin}
                                statusConfig={statusConfig}
                                onTimeScroll={handleTimeScroll}
                                onEventClick={(e, res) => res.transporterId === transporterId && handleEditClick(res)}
                            />

                            <DragOverlay dropAnimation={null}>
                                {dragActiveItem ? (
                                    <TimelineEvent
                                        reservation={{
                                            ...dragActiveItem,
                                            statusConfig: statusConfig[dragActiveItem.status]
                                        }}
                                        pxPerMin={pxPerMin}
                                        visibleStart={dayjs(selectedDate).hour(viewStartHour).toDate()}
                                        isDraggingOverlay
                                        totalLanes={1}
                                        laneIndex={0}
                                    />
                                ) : null}
                            </DragOverlay>
                        </DndContext>
                    </div>
                )}
            </div>

            {/* Action Modal */}
            <Modal
                isOpen={isActionModalOpen}
                onClose={() => setIsActionModalOpen(false)}
                title={actionType === 'approve' ? 'Rezervasyon Onayla' : 'Rezervasyon İptal Et'}
            >
                <form onSubmit={handleConfirmAction} className="p-md">
                    <p style={{ color: 'var(--text-primary)', marginBottom: '20px' }}>
                        {selectedRes && (
                            <>
                                <strong style={{ color: 'var(--primary-color)' }}>{selectedRes.reservationNumber || selectedRes.referenceNumber}</strong> nolu rezervasyonu
                                {actionType === 'approve' ? ' onaylamak' : ' iptal etmek'} üzeresiniz.
                            </>
                        )}
                    </p>

                    <div className="form-group">
                        <label className="form-label" style={{ color: 'var(--text-secondary)' }}>Notlar (Opsiyonel)</label>
                        <textarea
                            className="glass-input w-full"
                            rows={3}
                            placeholder={actionType === 'approve' ? 'Sürücü/Plaka bilgisi yakında girilecek...' : 'Müsaitlik sorunu nedeniyle...'}
                            value={note}
                            onChange={e => setNote(e.target.value)}
                        />
                    </div>

                    <div className="flex justify-end gap-md mt-xl">
                        <Button type="button" variant="secondary" onClick={() => setIsActionModalOpen(false)}>Vazgeç</Button>
                        <Button
                            type="submit"
                            variant={actionType === 'approve' ? 'primary' : 'danger'}
                        >
                            {actionType === 'approve' ? 'Onayla' : 'İptal Et'}
                        </Button>
                    </div>
                </form>
            </Modal>

            {/* Edit Modal - Reusing the complex business modal */}
            {isEditModalOpen && (
                <ReservationModal
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    initialData={selectedRes}
                    onSave={handleEditSave}
                    onDelete={() => handleEditDelete(selectedRes.id)}
                    userData={userData}
                    tenantId={user?.tenantId}
                    docks={docks}
                />
            )}

            <ConfirmDialog
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig(prev => ({ ...prev, isOpen: false }))}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </div>
    );
};

export default TransporterReservations;
